import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { EmptyRow, PanelTitle, Table, fmtDate, money } from "../office/ui";

const PLATFORMS = ["CHALAN", "MULTICONTACTOS", "OPINADERO", "SIN FRAUDE", "SYNAPSIS"];

export const ClientSalesPanel = () => {
  const [form, setForm] = useState({ platform: "OPINADERO", client: "", promoter: "", units: 1, sale_date: new Date().toISOString().slice(0, 10), note: "" });
  const [matches, setMatches] = useState([]);
  const [promoter, setPromoter] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => api.get("/admin/client-sales", { params: { q } }).then(({ data }) => setItems(data)), [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    if (promoter || !form.promoter.trim()) return undefined;
    const t = setTimeout(() => api.get("/admin/distributors", { params: { q: form.promoter } }).then(({ data }) => setMatches(data.slice(0, 6))), 300);
    return () => clearTimeout(t);
  }, [form.promoter, promoter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!promoter) return toast.error("Selecciona al promotor (distribuidor) de la lista");
    setBusy(true);
    try {
      await api.post("/admin/client-sales", { platform: form.platform, client: form.client, promoter_id: promoter.dist_id, units: Number(form.units), sale_date: form.sale_date, note: form.note });
      toast.success(`Venta registrada: cuenta como ${form.units} código(s) de $20 para ${promoter.name}`);
      setForm((f) => ({ ...f, client: "", promoter: "", units: 1, note: "" }));
      setPromoter(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar la venta de ${s.client} (${s.platform})?`)) return;
    await api.delete(`/admin/client-sales/${s.id}`);
    load();
  };

  return (
    <div data-testid="client-sales-panel">
      <PanelTitle title="Clientes" text="Captura los pagos de clientes (no distribuidores) que validaste en cada plataforma. Cada captura cuenta como una compra de $20 USD para el promotor: suma a sus ventas personales y reparte $1 a 5 niveles en las Ganancias Mensuales." />
      <form onSubmit={submit} className="mt-6 grid gap-4 rounded-[1.5rem] border border-ink/10 bg-white p-5 md:grid-cols-6" data-testid="client-sale-form">
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Plataforma</label>
          <select value={form.platform} onChange={(e) => set("platform", e.target.value)} className="h-11 w-full rounded-xl border border-ink/15 bg-white px-3 text-sm" data-testid="sale-platform">
            {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Cliente</label>
          <Input required value={form.client} onChange={(e) => set("client", e.target.value)} placeholder="Nombre, negocio o referencia" className="h-11 bg-white" data-testid="sale-client" />
        </div>
        <div className="relative md:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Promotor (ID o nombre)</label>
          {promoter ? (
            <div className="flex h-11 items-center justify-between rounded-xl border border-leaf/50 bg-leaf/10 px-3 text-sm" data-testid="sale-promoter-selected"><span>{promoter.name} <span className="font-mono text-xs">({promoter.dist_id})</span></span><button type="button" onClick={() => setPromoter(null)} className="text-xs underline">Cambiar</button></div>
          ) : (
            <>
              <Input value={form.promoter} onChange={(e) => set("promoter", e.target.value)} placeholder="Ej. 00001 o Ricardo" className="h-11 bg-white" data-testid="sale-promoter" />
              {matches.length > 0 && form.promoter && (
                <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-ink/10 bg-white shadow-lg">
                  {matches.map((m) => <li key={m.dist_id}><button type="button" onClick={() => { setPromoter(m); setMatches([]); }} className="flex w-full justify-between px-3 py-2 text-left text-sm hover:bg-paper" data-testid={`sale-promoter-option-${m.dist_id}`}><span>{m.name}</span><span className="font-mono text-xs">{m.dist_id}</span></button></li>)}
                </ul>
              )}
            </>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Códigos</label>
          <Input type="number" min={1} max={100} value={form.units} onChange={(e) => set("units", e.target.value)} className="h-11 bg-white" data-testid="sale-units" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Fecha de pago</label>
          <Input type="date" value={form.sale_date} onChange={(e) => set("sale_date", e.target.value)} className="h-11 bg-white" data-testid="sale-date" />
        </div>
        <div className="md:col-span-3">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-ink/70">Nota (opcional)</label>
          <Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Referencia del pago en la plataforma" className="h-11 bg-white" data-testid="sale-note" />
        </div>
        <div className="flex items-end md:col-span-1">
          <button type="submit" disabled={busy} className="btn-ink !h-11 w-full !py-0 !text-sm disabled:opacity-60" data-testid="sale-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Registrar</>}</button>
        </div>
      </form>

      <div className="mt-8 flex items-center justify-between gap-3">
        <h3 className="font-display text-xl font-semibold">Ventas a clientes registradas</h3>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente, promotor o plataforma" className="h-10 w-72 bg-white" data-testid="sales-search" />
      </div>
      <Table headers={["Fecha", "Plataforma", "Cliente", "Promotor", "Códigos", "Monto", "Nota", ""]} testId="client-sales-table" minWidth="min-w-[900px]">
        {!items && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {items && !items.length && <EmptyRow cols={8} text="Aún no has registrado ventas a clientes." testId="sales-empty" />}
        {items?.map((s, i) => (
          <tr key={s.id} className="border-b border-ink/5 last:border-0" data-testid={`sale-row-${i}`}>
            <td className="px-4 py-3 text-ink/70">{fmtDate(s.sale_date)}</td>
            <td className="px-4 py-3 font-semibold">{s.platform}</td>
            <td className="px-4 py-3">{s.client}</td>
            <td className="px-4 py-3">{s.promoter_name} <span className="font-mono text-xs text-ink/60">({s.promoter_id})</span></td>
            <td className="px-4 py-3">{s.units}</td>
            <td className="px-4 py-3 font-semibold">{money(s.amount)}</td>
            <td className="px-4 py-3 text-xs text-ink/60">{s.note}</td>
            <td className="px-4 py-3 text-right"><button onClick={() => remove(s)} className="grid h-8 w-8 place-items-center rounded-full text-coral hover:bg-coral/10" data-testid={`sale-delete-${i}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
